import { getRenderingEngine, Types } from "@cornerstonejs/core";
import { ViewportType } from "@cornerstonejs/core/enums";

/**
 * Handle manual zoom for both 2D and 3D viewports
 * @param renderingEngineId The ID of the rendering engine
 * @param viewportId The ID of the viewport
 * @param direction The zoom direction ("in" or "out")
 */
export const manualZoom = (
  renderingEngineId: string,
  viewportId: string,
  direction: "in" | "out"
): void => {
  const renderingEngine = getRenderingEngine(renderingEngineId);
  if (!renderingEngine) return;

  const viewport = renderingEngine.getViewport(viewportId);
  if (!viewport) return;

  // Check if it's a 2D stack viewport or 3D volume viewport
  if (viewport.type === ViewportType.STACK) {
    // For 2D stack viewports, use zoom method
    const stackViewport = viewport as Types.IStackViewport;
    const currentZoom = stackViewport.getZoom();
    const zoomSpeed = 0.15;
    const zoomFactor = direction === "in" ? 1 + zoomSpeed : 1 - zoomSpeed;
    const newZoom = currentZoom * zoomFactor;
    
    // Prevent zooming out too much or in too much
    const minZoom = 0.1;
    const maxZoom = 10;
    const clampedZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));
    
    stackViewport.setZoom(clampedZoom);
    stackViewport.render();
  } else {
    // For 3D volume viewports, use camera zoom
    const camera = viewport.getRenderer().getActiveCamera();
    const renderer = viewport.getRenderer();

    // Calculate the zoom factor
    const zoomSpeed = 0.1;
    const zoomAmount = direction === "in" ? zoomSpeed : -zoomSpeed;
    const newZoomFactor = 1.0 + zoomAmount;
    camera.zoom(newZoomFactor);

    // Reset clipping range to ensure proper rendering
    renderer.resetCameraClippingRange();

    // Render the scene
    viewport.render();
  }
};

/**
 * Setup viewport configurations
 * @param renderingEngine The rendering engine
 * @param viewports Array of viewport configurations
 */
export const setupViewports = (renderingEngine: any, viewports: any[]) => {
  renderingEngine.setViewports(viewports);
};

/**
 * Update window width and level for the 2D viewports
 * @param renderingEngineId The ID of the rendering engine
 * @param windowWidth The new window width value
 * @param windowLevel The new window level value
 */
export const updateWindowLevel = (
  renderingEngineId: string,
  windowWidth: number,
  windowLevel: number
): void => {
  const renderingEngine = getRenderingEngine(renderingEngineId);
  if (!renderingEngine) return;

  // Array of 2D viewport IDs to update
  const viewportIds = ["CT_AXIAL", "CT_SAGITTAL", "CT_CORONAL"];

  console.log("=== UPDATING WINDOW LEVEL ===");
  console.log("Input WW/WL:", { windowWidth, windowLevel });
  
  // Calculate correct VOI range
  const upper = windowLevel + windowWidth / 2;
  const lower = windowLevel - windowWidth / 2;
  
  console.log("Calculated VOI Range:", { upper, lower });

  // Update each viewport
  viewportIds.forEach((viewportId) => {
    const viewport = renderingEngine.getViewport(
      viewportId
    ) as Types.IStackViewport;

    // Set the correct VOI range
    viewport.setProperties({
      voiRange: { upper, lower },
    });

    // Render the viewport with the new settings
    viewport.render();
  });
};
